import { useEffect } from 'react';
import { create } from 'zustand';

interface ReloadGuardState {
  /** Number of components currently holding unsaved work. */
  activeGuards: number;
  addGuard: () => void;
  removeGuard: () => void;
}

/**
 * Tracks how many surfaces currently have unsaved work that must NOT be
 * interrupted by an automatic reload (the manual "Update Now" banner is still
 * available — auto-apply is what this blocks).
 */
export const useReloadGuardStore = create<ReloadGuardState>((set) => ({
  activeGuards: 0,
  addGuard: () => set((s) => ({ activeGuards: s.activeGuards + 1 })),
  removeGuard: () => set((s) => ({ activeGuards: Math.max(0, s.activeGuards - 1) })),
}));

/** Non-reactive read for use inside plain functions (e.g. reload-safety). */
export function hasActiveReloadGuard(): boolean {
  return useReloadGuardStore.getState().activeGuards > 0;
}

/**
 * Opt a form (or any flow with unsaved state) into auto-reload protection.
 * While `isDirty` is true, the tab will not be auto-reloaded for a pending
 * service-worker update. Adoption is incremental — call this from surfaces
 * where losing in-progress input would be costly.
 */
export function useUnsavedReloadGuard(isDirty: boolean): void {
  useEffect(() => {
    if (!isDirty) return;
    const { addGuard, removeGuard } = useReloadGuardStore.getState();
    addGuard();
    return () => removeGuard();
  }, [isDirty]);
}
